/**
 * GuidedLogCard — multi-step guided test-kit log flow.
 *
 * Steps: param → kit → value → done (SaveCelebration).
 * Replaces QuickLogPanel in the "quick" tab of TestKitsPage.
 */
import { useCallback, useMemo, useState } from 'react';

import { ALL_TEST_KITS, getIdealRangeForParameter } from '@reefapp/shared';
import type { ParameterKey, TestKit } from '@reefapp/shared';

import type { QuickLogResult, QuickLogStore } from '../../pages/testkits/QuickLogPanel';
import { EMPTY_STORE } from '../../pages/testkits/QuickLogPanel';
import { useServerBackedState } from '../../hooks/useServerBackedState';
import { useTestKitLogs } from '../useTestKitLogs';
import { coachSentence } from './coachSentence';
import { KitPicker } from './KitPicker';
import { LiveTrendPreview } from './LiveTrendPreview';
import { SaveCelebration } from './SaveCelebration';
import { ValueSliderInput } from './ValueSliderInput';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Step = 'param' | 'kit' | 'value' | 'done';

interface ParamMeta {
  key: ParameterKey;
  label: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const QUICK_PARAMS: ReadonlyArray<ParamMeta> = [
  { key: 'alkalinity', label: 'Alkalinity' },
  { key: 'calcium',    label: 'Calcium'    },
  { key: 'magnesium',  label: 'Magnesium'  },
  { key: 'nitrate',    label: 'Nitrate'    },
  { key: 'phosphate',  label: 'Phosphate'  },
  { key: 'ph',         label: 'pH'         },
  { key: 'salinity',   label: 'Salinity'   },
  { key: 'ammonia',    label: 'Ammonia'    },
  { key: 'nitrite',    label: 'Nitrite'    },
];

/** Widen ideal-range margin by 50% for the slider bounds. */
function sliderBounds(
  paramKey: ParameterKey,
  kit: TestKit | null,
): { min: number; max: number; step: number } {
  // Prefer kit's own idealRange, then shared catalog.
  const rangeSource =
    kit?.idealRange ?? getIdealRangeForParameter(paramKey) ?? null;

  if (!rangeSource) {
    return { min: 0, max: 100, step: 0.1 };
  }

  const { min, max } = rangeSource;
  const margin = max - min;
  const widen = margin * 0.5;

  // Keep min at 0 at the lower end to avoid negatives on parameters that
  // can't be negative (e.g. alkalinity, calcium, salinity...).
  return {
    min: Math.max(0, min - widen),
    max: max + widen,
    step: margin >= 10 ? 0.5 : 0.01,
  };
}

// ---------------------------------------------------------------------------
// Helper — group kits by parameter
// ---------------------------------------------------------------------------

function groupKitsByParam(): Map<ParameterKey, TestKit[]> {
  const map = new Map<ParameterKey, TestKit[]>();
  for (const kit of ALL_TEST_KITS) {
    const list = map.get(kit.parameterKey);
    if (list) list.push(kit);
    else map.set(kit.parameterKey, [kit]);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GuidedLogCard(): JSX.Element {
  const { addLog, logs } = useTestKitLogs();
  const { value: store, setValue: setStore } =
    useServerBackedState<QuickLogStore>('test-results', EMPTY_STORE);

  const kitsByParam = useMemo(groupKitsByParam, []);

  // ---- navigation state ---------------------------------------------------
  const [step, setStep] = useState<Step>('param');
  const [selectedParam, setSelectedParam] = useState<ParameterKey | null>(null);
  const [selectedKit,   setSelectedKit]   = useState<TestKit | null>(null);
  const [value,         setValue]         = useState<number>(0);
  const [sentence,      setSentence]      = useState('');

  // ---- derived values -----------------------------------------------------
  const activeKits: ReadonlyArray<TestKit> = selectedParam
    ? (kitsByParam.get(selectedParam) ?? [])
    : [];

  const bounds = sliderBounds(selectedParam ?? 'alkalinity', selectedKit);

  const historicalLogs = useMemo(
    () =>
      selectedParam
        ? logs.filter(
            (l) => l.parameterKey === selectedParam && typeof l.result === 'number',
          )
        : [],
    [logs, selectedParam],
  );

  // ---- handlers -----------------------------------------------------------
  const handleSelectParam = useCallback((key: ParameterKey) => {
    setSelectedParam(key);
    setSelectedKit(null);
    setValue(0);
    setStep('kit');
  }, []);

  const handleSelectKit = useCallback(
    (kit: TestKit) => {
      setSelectedKit(kit);
      // Default value to center of ideal range
      const range = kit.idealRange ?? getIdealRangeForParameter(kit.parameterKey);
      setValue(range ? (range.min + range.max) / 2 : 0);
      setStep('value');
    },
    [],
  );

  const handleSave = useCallback(() => {
    if (!selectedParam || !selectedKit) return;
    const unit = selectedKit.resultUnit;

    const coachMsg = coachSentence({ parameterKey: selectedParam, value, unit });
    setSentence(coachMsg);

    // (a) Feed shared log store (Logs tab) — include coach sentence for timeline
    addLog({
      brand:         selectedKit.brand,
      testName:      selectedKit.name,
      kitSlug:       selectedKit.slug,
      parameter:     selectedKit.parameter,
      parameterKey:  selectedParam,
      result:        value,
      unit,
      coachSentence: coachMsg,
    });

    // (b) Durable copy on Pi + remember kit for param
    const entry: QuickLogResult = {
      id:           crypto.randomUUID(),
      date:         new Date().toISOString(),
      parameterKey: selectedParam,
      parameter:    selectedKit.parameter,
      kitSlug:      selectedKit.slug,
      brand:        selectedKit.brand,
      testName:     selectedKit.name,
      result:       value,
      unit,
    };
    setStore({
      lastKitByParam: {
        ...store.lastKitByParam,
        [selectedParam]: selectedKit.slug,
      },
      entries: [entry, ...store.entries],
    });

    setStep('done');
  }, [
    selectedParam,
    selectedKit,
    value,
    addLog,
    setStore,
    store.lastKitByParam,
    store.entries,
  ]);

  const handleReset = useCallback(() => {
    setStep('param');
    setSelectedParam(null);
    setSelectedKit(null);
    setValue(0);
    setSentence('');
  }, []);

  const handleBack = useCallback(() => {
    if (step === 'kit')   setStep('param');
    if (step === 'value') setStep('kit');
  }, [step]);

  // ---- render -------------------------------------------------------------

  const paramLabel =
    QUICK_PARAMS.find((p) => p.key === selectedParam)?.label ?? '';

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="bg-card border border-border rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
          {step !== 'param' && step !== 'done' && (
            <button
              type="button"
              onClick={handleBack}
              aria-label="Back"
              className="text-muted-foreground hover:text-foreground transition-colors text-sm mr-1"
            >
              ←
            </button>
          )}
          <h2 className="text-lg font-bold text-foreground flex-1">
            {step === 'param' && 'Log a Test Result'}
            {step === 'kit'   && `${paramLabel} — Pick your kit`}
            {step === 'value' && `${paramLabel} Reading`}
            {step === 'done'  && 'Saved!'}
          </h2>
          {/* Step dots */}
          {step !== 'done' && (
            <div className="flex gap-1.5">
              {(['param', 'kit', 'value'] as const).map((s) => (
                <span
                  key={s}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    s === step ? 'bg-accent' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {/* ─── STEP 1: param picker ─── */}
          {step === 'param' && (
            <div className="grid grid-cols-3 gap-2">
              {QUICK_PARAMS.filter(
                (p) => (kitsByParam.get(p.key)?.length ?? 0) > 0,
              ).map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handleSelectParam(p.key)}
                  className="rounded-xl px-3 py-4 text-sm font-semibold bg-muted
                             hover:bg-accent hover:text-accent-foreground
                             active:scale-95 transition-all duration-150
                             focus-visible:outline focus-visible:outline-2
                             focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          {/* ─── STEP 2: kit picker ─── */}
          {step === 'kit' && (
            <KitPicker kits={activeKits} onSelect={handleSelectKit} />
          )}

          {/* ─── STEP 3: value entry ─── */}
          {step === 'value' && selectedParam && selectedKit && (
            <div className="space-y-6">
              <ValueSliderInput
                value={value}
                onChange={setValue}
                min={bounds.min}
                max={bounds.max}
                step={bounds.step}
                unit={selectedKit.resultUnit}
              />

              {/* Live trend */}
              {historicalLogs.length > 0 && (
                <LiveTrendPreview
                  logs={historicalLogs}
                  parameterKey={selectedParam}
                  currentValue={value}
                  className="rounded-xl overflow-hidden"
                />
              )}

              <button
                type="button"
                onClick={handleSave}
                className="w-full py-3 rounded-xl bg-accent text-accent-foreground
                           font-semibold text-base hover:opacity-90
                           active:scale-[0.98] transition-all duration-150
                           focus-visible:outline focus-visible:outline-2
                           focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                Save
              </button>
            </div>
          )}

          {/* ─── STEP 4: celebration ─── */}
          {step === 'done' && (
            <SaveCelebration
              sentence={sentence}
              onDismiss={handleReset}
            />
          )}
        </div>
      </div>
    </div>
  );
}
